import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import useDSApplicationMode from '../../hooks/useDSApplicationMode'
import StationMenu from '../StationMenu/StationMenu'
import ExperienceNavControls from '../ExperienceNavControls/ExperienceNavControls'
import useKioskNavigationPolicy, { KIOSK_NAV_VARIANTS } from '../ExperienceNavControls/useKioskNavigationPolicy'
import useKioskIdle from '../../hooks/useKioskIdle'
import { useLogout } from '../../hooks/discoveryCenter/centerHooks'
import { getKioskConfig, undockKiosk } from '../../helpers/kioskConfig'
import useDSCChromeStateSynchronization from './useDSCChromeStateSynchronization'
import { experienceNavPropTypes } from '../ExperienceNavControls/navigationItemPropType'

/**
 * The chrome every experience gets, mounted once per experience (by
 * ExperienceShell/ExperienceChrome or directly):
 *
 * - WEB: the floating ExperienceNavControls, which decide their own visibility
 *   from what the experience declared. The live production experiences declare
 *   nothing until discovery_prototypes is on (useLegacyNavHomePath and their
 *   flag-gated Back registrations), so they show none of the new controls
 *   before the rollout.
 * - STATION before the rollout: the legacy StationMenu bar, which runs its own
 *   idle logout.
 * - STATION under the rollout: the shared controls are the whole kiosk chrome
 *   (a Leave Kiosk edge tab on every screen), and this component owns idle
 *   logout - mounting once per experience is what keeps that timer single.
 *
 * `hideKioskHeader` is STATION-only: a screen that must show no kiosk chrome
 * at all (the code-entry attract) gets nothing and runs no idle timer.
 */
export default function ConditionalHeader({ hideKioskHeader = false, activeExperienceId = 'none', ...navProps }) {
  const appMode = useDSApplicationMode()
  const { kioskNavVariant, sharedNavOwnsIdleTimer } = useKioskNavigationPolicy({ hideKioskHeader })
  useDSCChromeStateSynchronization({ activeExperienceId, appMode })

  const conf = getKioskConfig()
  const [logout, logoutState] = useLogout()

  useEffect(() => {
    if (sharedNavOwnsIdleTimer && logoutState.complete) {
      undockKiosk({ resetContainerSession: true })
    }
  }, [logoutState.complete, sharedNavOwnsIdleTimer])

  useKioskIdle(sharedNavOwnsIdleTimer ? (conf?.timeOut ?? 600) : null, () => {
    logout()
  })

  if (appMode === 'WEB') return <ExperienceNavControls {...navProps} />

  if (kioskNavVariant === KIOSK_NAV_VARIANTS.LEGACY_STATION_MENU) {
    return <StationMenu activeExperienceId={activeExperienceId} />
  }

  if (kioskNavVariant !== KIOSK_NAV_VARIANTS.SHARED_CONTROLS) return null

  return <ExperienceNavControls {...navProps} />
}

ConditionalHeader.propTypes = {
  hideKioskHeader: PropTypes.bool,
  activeExperienceId: PropTypes.string,
  ...experienceNavPropTypes,
}
